import { useEffect, useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { DropResult } from '@hello-pangea/dnd';
import { Modal, notification } from 'antd';
import { useCustomizationsQuery, CUSTOMIZATIONS_KEY } from './useCustomizationsQuery';
import { useCustomizationMutations } from './useCustomizationMutations';
import { useDeleteOption } from './useOptionsMutations';
import { Customization, CustomizationData } from '../types';

export const useCustomizations = (
  selectedCustomizationId: string | number | undefined,
  tabIndex: number | undefined,
) => {
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [allCustomizations, setAllCustomizations] = useState<Customization[]>([]);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [selectedItem, setSelectedItem] = useState<number | null>(null);
  const [customizationData, setCustomizationData] = useState<CustomizationData | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [customizationToDelete, setCustomizationToDelete] = useState<Customization | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [duplicatingId, setDuplicatingId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [customizationOrder, setCustomizationOrder] = useState<(number | string)[]>([]);
  const [refreshing, setRefreshing] = useState(false);
  const [modalRefreshing, setModalRefreshing] = useState(false);
  const [editTargetOptionId, setEditTargetOptionId] = useState<number | string | null>(null);
  const enabled = tabIndex === 5;
  const { data, isFetching, refetch } = useCustomizationsQuery({ page, search: searchQuery }, { enabled });

  const {
    createMutation,
    updateMutation,
    deleteMutation,
    cloneMutation,
    bulkOrderMutation,
    updateOptionSoldOutMutation,
  } = useCustomizationMutations();
  const deleteOptionMutation = useDeleteOption();

  // Accumulate pages — replace on page 1, append on higher pages
  useEffect(() => {
    if (!data) return;
    if (page === 1) {
      setAllCustomizations(data.data);
    } else {
      setAllCustomizations((prev) => {
        const existingIds = new Set(prev.map((c) => c.id));
        return [...prev, ...data.data.filter((c) => !existingIds.has(c.id))];
      });
    }
    setLastUpdated(new Date().toLocaleString());
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear refreshing flags when fetching finishes
  useEffect(() => {
    if (!isFetching) {
      if (refreshing) setRefreshing(false);
      if (modalRefreshing) setModalRefreshing(false);
    }
  }, [isFetching, refreshing, modalRefreshing]);

  // Update drag-drop order array — only reset when the set of IDs changes (add/delete),
  // not when unrelated cache patches (e.g. options reorder) trigger allCustomizations to update
  useEffect(() => {
    const sorted = [...allCustomizations].sort((a, b) => (a.sequence_number || 0) - (b.sequence_number || 0));
    const newIds = sorted.map((c) => c.id);
    setCustomizationOrder((prev) => {
      if (prev.length === newIds.length && newIds.every((id) => prev.includes(id))) return prev;
      return newIds;
    });
  }, [allCustomizations]);

  // Keep customizationData in sync after refetch so options reflect latest picture URLs
  useEffect(() => {
    if (!selectedItem || !isEditModalOpen) return;
    const found = allCustomizations.find((c) => c.id === selectedItem);
    if (!found) return;
    setCustomizationData({
      customization: found.customization,
      options: found.option,
      note: found.note,
      max_quantity: found.max_quantity,
      min_quantity: found.min_quantity,
      enable_quantity_control: found.enable_quantity_control,
      multiple_selections: found.multiple_selections,
      sold_out: Boolean(found.sold_out),
      multiple_selections_quantity: found.multiple_selections_quantity,
      related_items: found.related_items || [],
    });
  }, [allCustomizations]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced search
  useEffect(() => {
    const trimmed = searchInput.trim();
    if (trimmed === searchQuery.trim()) {
      setIsSearching(false);
      return;
    }
    setIsSearching(true);
    const timer = setTimeout(() => {
      setSearchQuery(trimmed);
      setPage(1);
      setIsSearching(false);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pre-select by ID from props
  useEffect(() => {
    if (selectedCustomizationId && allCustomizations.length > 0) {
      const found = allCustomizations.find((c) => c.id.toString() === selectedCustomizationId.toString());
      if (found) {
        setSelectedItem(found.id);
        setCustomizationData({
          customization: found.customization,
          options: found.option,
          note: found.note,
          max_quantity: found.max_quantity,
          min_quantity: found.min_quantity,
          enable_quantity_control: found.enable_quantity_control,
          multiple_selections: found.multiple_selections,
          sold_out: Boolean(found.sold_out),
          multiple_selections_quantity: found.multiple_selections_quantity,
          related_items: found.related_items || [],
        });
      }
    }
  }, [selectedCustomizationId, allCustomizations]);

  // Custom events from sub-components
  useEffect(() => {
    const onUpdated = () => { setPage(1); queryClient.invalidateQueries({ queryKey: [CUSTOMIZATIONS_KEY] }); };
    const onCreated = () => { setPage(1); queryClient.invalidateQueries({ queryKey: [CUSTOMIZATIONS_KEY] }); };
    const onReordered = () => { queryClient.invalidateQueries({ queryKey: [CUSTOMIZATIONS_KEY] }); };

    window.addEventListener('customizationUpdated', onUpdated);
    window.addEventListener('customizationCreated', onCreated);
    window.addEventListener('optionsReordered', onReordered);
    return () => {
      window.removeEventListener('customizationUpdated', onUpdated);
      window.removeEventListener('customizationCreated', onCreated);
      window.removeEventListener('optionsReordered', onReordered);
    };
  }, [queryClient]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    if (page === 1) refetch();
    else setPage(1);
  }, [page, refetch]);

  const handleModalRefresh = useCallback(async () => {
    setModalRefreshing(true);
    if (page === 1) refetch();
    else setPage(1);
  }, [page, refetch]);

  const handleCreate = useCallback(() => {
    setSelectedItem(null);
    setShowCreateForm(true);
    const nextSeq = allCustomizations.length === 0
      ? 1
      : Math.max(...allCustomizations.map((c) => c.sequence_number || 0)) + 1;
    setCustomizationData({
      customization: '', options: [], note: '',
      sequence_number: nextSeq,
      max_quantity: 0, min_quantity: 0,
      multiple_selections: false, sold_out: false,
      multiple_selections_quantity: 0, related_items: [],
    });
  }, [allCustomizations]);

  const handleCloseCreateDrawer = useCallback(() => {
    setShowCreateForm(false);
    setCustomizationData(null);
  }, []);

  const handleCreateSubmit = useCallback(async (formData: CustomizationData) => {
    try {
      const result = await createMutation.mutateAsync(formData);
      notification.success({ message: 'Success', description: 'Customization created successfully!' });
      window.dispatchEvent(new CustomEvent('customizationCreated', { detail: { customization: result } }));
      setShowCreateForm(false);
      setCustomizationData(null);
      setSelectedItem(null);
      setPage(1);
    } catch (error: any) {
      notification.error({ message: 'Error', description: error?.message || 'Failed to create customization.' });
    }
  }, [createMutation]);

  const handleItemClick = useCallback((customization: Customization) => {
    setShowCreateForm(false);
    setSelectedItem(customization.id);
    setCustomizationData({
      customization: customization.customization,
      options: customization.option,
      note: customization.note,
      max_quantity: customization.max_quantity,
      min_quantity: customization.min_quantity,
      enable_quantity_control: customization.enable_quantity_control,
      multiple_selections: customization.multiple_selections,
      sold_out: Boolean(customization.sold_out),
      multiple_selections_quantity: customization.multiple_selections_quantity,
      related_items: customization.related_items || [],
    });
    setIsEditModalOpen(true);
  }, []);

  const handleCloseEditModal = useCallback(() => {
    setIsEditModalOpen(false);
    setSelectedItem(null);
    setCustomizationData(null);
    setEditTargetOptionId(null);
  }, []);

  const handleModalInputChange = useCallback((field: string, value: any) => {
    setCustomizationData((prev) => prev ? { ...prev, [field]: value } : prev);
  }, []);

  const handleDeleteClick = useCallback((customization: Customization) => {
    setCustomizationToDelete(customization);
    setShowDeleteDialog(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!customizationToDelete) return;
    setIsDeleting(true);
    try {
      await deleteMutation.mutateAsync(customizationToDelete.id);
      notification.success({ message: 'Success', description: `"${customizationToDelete.customization}" has been deleted.` });
      window.dispatchEvent(new CustomEvent('customizationDeleted', { detail: { customization: customizationToDelete } }));
      if (selectedItem === customizationToDelete.id) {
        setSelectedItem(null);
        setCustomizationData(null);
      }
      setShowDeleteDialog(false);
      setCustomizationToDelete(null);
      setPage(1);
    } catch (error: any) {
      notification.error({ message: 'Error', description: error?.message || 'Failed to delete customization.' });
    } finally {
      setIsDeleting(false);
    }
  }, [customizationToDelete, selectedItem, deleteMutation]);

  const handleDeleteCancel = useCallback(() => {
    setShowDeleteDialog(false);
    setCustomizationToDelete(null);
  }, []);

  const handleDuplicateClick = useCallback(async (customization: Customization) => {
    setDuplicatingId(customization.id);
    try {
      const result = await cloneMutation.mutateAsync(customization.id);
      notification.success({ message: 'Success', description: `"${customization.customization}" has been duplicated.` });
      window.dispatchEvent(new CustomEvent('customizationCreated', { detail: { customization: result } }));
    } catch (error: any) {
      notification.error({ message: 'Error', description: error?.message || 'Failed to clone customization.' });
    } finally {
      setDuplicatingId(null);
    }
  }, [cloneMutation]);

  const toggleAccordion = useCallback((index: number) => {
    setExpandedIndex((prev) => (prev === index ? null : index));
  }, []);

  const submitSoldOut = useCallback(async (option: any, updatedOption: any) => {
    try {
      await updateOptionSoldOutMutation.mutateAsync({ option, updatedOption });
      notification.success({ message: 'Success', description: 'Option updated successfully.' });
      await handleRefresh();
    } catch (error: any) {
      notification.error({ message: 'Error', description: error?.message || 'Failed to update option.' });
    }
  }, [updateOptionSoldOutMutation, handleRefresh]);

  const handleEditOptionFromCard = useCallback((option: any, customization: Customization) => {
    setSelectedItem(customization.id);
    setCustomizationData({
      customization: customization.customization,
      options: customization.option,
      note: customization.note,
      max_quantity: customization.max_quantity,
      min_quantity: customization.min_quantity,
      enable_quantity_control: customization.enable_quantity_control,
      multiple_selections: customization.multiple_selections,
      sold_out: Boolean(customization.sold_out),
      multiple_selections_quantity: customization.multiple_selections_quantity,
      related_items: customization.related_items || [],
    });
    setEditTargetOptionId(option.id);
    setIsEditModalOpen(true);
  }, []);

  const handleDeleteOptionFromCard = useCallback((option: any) => {
    Modal.confirm({
      title: 'Delete Option',
      content: `Are you sure you want to delete "${option.option}"?`,
      okText: 'Delete',
      cancelText: 'Cancel',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await deleteOptionMutation.mutateAsync(option.id);
          notification.success({ message: 'Success', description: 'Option deleted successfully.' });
          await handleRefresh();
        } catch (err: any) {
          notification.error({ message: 'Error', description: err?.message || 'Failed to delete option.' });
        }
      },
    });
  }, [deleteOptionMutation, handleRefresh]);

  const handleDragEnd = useCallback((result: DropResult) => {
    const { source, destination } = result;
    if (!destination || source.index === destination.index) return;

    const prevOrder = Array.from(customizationOrder);
    const newOrder = Array.from(customizationOrder);
    const [moved] = newOrder.splice(source.index, 1);
    newOrder.splice(destination.index, 0, moved);
    setCustomizationOrder(newOrder);

    const updates = newOrder
      .map((id, index) => {
        const c = allCustomizations.find((c) => c.id === id);
        return c ? { id: c.id, sequence_number: index } : null;
      })
      .filter(Boolean) as { id: number; sequence_number: number }[];

    if (updates.length === 0) return;

    bulkOrderMutation.mutateAsync(updates)
      .then(() => {
        notification.success({ message: 'Success', description: 'Customization order updated.' });
      })
      .catch((error: any) => {
        setCustomizationOrder(prevOrder);
        notification.error({ message: 'Error', description: error?.message || 'Unable to update the customization order.' });
      });
  }, [customizationOrder, allCustomizations, bulkOrderMutation]);

  const handleSubmit = useCallback(async (submissionType: string, data: any) => {
    if (!selectedItem) return;
    try {
      if (submissionType === 'name') {
        if (!data?.customization?.trim()) {
          notification.error({ message: 'Validation Error', description: 'Customization name cannot be empty.' });
          return;
        }
        const updated = await updateMutation.mutateAsync({ id: selectedItem, data: { customization: data.customization.trim() }, type: 'name' });
        notification.success({ message: 'Success', description: 'Customization name updated successfully.' });
        window.dispatchEvent(new CustomEvent('customizationUpdated', { detail: { customization: updated } }));
        await handleRefresh();
      } else if (submissionType === 'options') {
        const updated = await updateMutation.mutateAsync({ id: selectedItem, data, type: 'options' });
        notification.success({ message: 'Success', description: 'Options updated successfully.' });
        window.dispatchEvent(new CustomEvent('customizationUpdated', { detail: { customization: updated } }));
        await handleRefresh();
        setSelectedItem(null);
        setCustomizationData(null);
      } else if (submissionType === 'addtoitems') {
        const updated = await updateMutation.mutateAsync({ id: selectedItem, data, type: 'addtoitems' });
        notification.success({ message: 'Success', description: 'Items updated successfully.' });
        window.dispatchEvent(new CustomEvent('customizationUpdated', { detail: { customization: updated } }));
        setSelectedItem(null);
        setCustomizationData(null);
      } else if (submissionType === 'customizationrules') {
        const updated = await updateMutation.mutateAsync({ id: selectedItem, data, type: 'customizationrules' });
        notification.success({ message: 'Success', description: 'Rules updated successfully.' });
        window.dispatchEvent(new CustomEvent('customizationUpdated', { detail: { customization: updated } }));
      }
    } catch (error: any) {
      notification.error({ message: 'Error', description: error?.message || 'Failed to update customization.' });
    }
  }, [selectedItem, updateMutation, handleRefresh]);

  const handleLoadMore = useCallback(() => {
    setPage((p) => p + 1);
  }, []);

  const loading = isFetching && !refreshing;
  const total = data?.pagination?.total ?? 0;
  const totalPages = data?.pagination?.totalPages ?? 1;
  const hasMore = page < totalPages;

  return {
    // State (read)
    page, searchInput, isSearching, allCustomizations, lastUpdated, isFetching,
    expandedIndex, selectedItem, customizationData, showCreateForm,
    showDeleteDialog, customizationToDelete, isEditModalOpen, duplicatingId,
    isDeleting, customizationOrder, refreshing, modalRefreshing, editTargetOptionId,
    // Derived
    loading, total, hasMore,
    // Setters needed directly in JSX
    setSearchInput,
    // Handlers
    handleRefresh, handleModalRefresh,
    handleCreate, handleCloseCreateDrawer, handleCreateSubmit,
    handleItemClick, handleCloseEditModal,
    handleModalInputChange,
    handleDeleteClick, handleDeleteConfirm, handleDeleteCancel,
    handleDuplicateClick,
    toggleAccordion,
    submitSoldOut,
    handleEditOptionFromCard, handleDeleteOptionFromCard,
    handleDragEnd,
    handleSubmit,
    handleLoadMore,
  };
};
